/* Presentation rules for rules and executions. Pure — no React, no fetch.
 *
 * Two judgements here are the ones worth guarding, and both are about not overstating a result:
 * `partial` must never read as success, and an execution that may not publish a score must never
 * look like one that can.
 */

import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type {
  Execution,
  ExecutionState,
  Rule,
  RuleBinding,
  RuleState,
  SampleMode,
  SampleState,
  Severity,
} from '../api/rules';

/** `draft` is `info`, not `warn`.
 *
 * A rule waiting for review is the expected state of a rule somebody just wrote, not a problem. Amber
 * on every draft is a screen full of false alarms on the day a team starts authoring.
 */
export function ruleTone(state: RuleState): BadgeTone {
  switch (state) {
    case 'active':
      return 'ok';
    case 'deprecated':
      return 'neutral';
    default:
      return 'info';
  }
}

/** Severity → tone. `blocker` and `error` are deliberately distinct.
 *
 * Both are failures, but only one stops a release, and a screen that renders them identically makes
 * an operator open each violation to find out which they are looking at.
 */
export function severityTone(severity: Severity): BadgeTone {
  switch (severity) {
    case 'blocker':
      return 'danger';
    case 'error':
      return 'warn';
    case 'warning':
      return 'unknown';
    default:
      return 'neutral';
  }
}

/** `partial` is `warn`, never `ok`.
 *
 * The run finished and did not see everything. Rendering it green erases the single fact the state
 * exists to carry, and a score built on it would claim coverage it does not have.
 *
 * `cancelled` is `neutral`, not `danger`: an operator stopping a run is a decision, not a fault, and
 * alerting on it trains people to ignore execution alerts.
 */
export function executionTone(state: ExecutionState): BadgeTone {
  switch (state) {
    case 'succeeded':
      return 'ok';
    case 'partial':
      return 'warn';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'neutral';
    default:
      return 'info';
  }
}

export function isTerminal(state: ExecutionState): boolean {
  return state !== 'pending' && state !== 'running';
}

/** How far through the fan-out a run is, as a fraction, or null when nothing has been queued.
 *
 * `null` rather than 0 for an empty fan-out, so a caller cannot render "0%" for a run that has no
 * tasks — which reads as stalled rather than as not-yet-planned.
 */
export function completion(execution: Execution): number | null {
  const { total, succeeded, failed, cancelled } = execution.progress;
  if (total === 0) return null;
  return (succeeded + failed + cancelled) / total;
}

/** A short, stable label for a plan hash, for a column that cannot hold 71 characters.
 *
 * The algorithm prefix is kept and the digest truncated, never the reverse: two runs are compared by
 * whether their hashes are equal, and dropping the prefix would make two differently-hashed plans
 * look comparable the day sha256 is replaced.
 */
export function shortPlanHash(planHash: string): string {
  const separator = planHash.indexOf(':');
  /* No separator means no algorithm prefix, which the backend refuses to store — so this branch is
   * only reachable from a malformed response, and passing it through unchanged is more honest than
   * inventing a prefix for it. */
  if (separator === -1) return planHash;
  const algorithm = planHash.slice(0, separator);
  const digest = planHash.slice(separator + 1);
  return `${algorithm}:${digest.slice(0, 10)}`;
}

/** Whether a score may be derived from this run, and why not when it may not.
 *
 * Returns the *reason* rather than a boolean, because a screen showing a disabled action without one
 * is a screen an operator files a bug against. `null` means it is permitted.
 */
export function scoreBlockedReason(execution: Execution): string | null {
  if (execution.may_publish_a_score) return null;
  if (!isTerminal(execution.state)) return 'rules.score.notFinished';
  return 'rules.score.notMeasured';
}

/** What the Score column offers on a run: the action, the reason it cannot, or nothing.
 *
 * **Three answers rather than a boolean, because two of them are not the same "no".** A run that
 * cannot yield a score has a *reason* the reader needs (`blocked`); a reader who may not compute one
 * gets no control and no explanation, because the reason is about them rather than about the run and
 * a disabled button saying "you lack a permission" on every row is noise. Only the third case is an
 * action.
 *
 * Here rather than in the JSX so it is testable without a QueryClient and a router — the house
 * pattern, and the reason `scoreBlockedReason` is here too.
 */
export type ScoreAction =
  | { readonly kind: 'compute' }
  | { readonly kind: 'blocked'; readonly reasonKey: string }
  | { readonly kind: 'none' };

export function scoreAction(execution: Execution, mayCompute: boolean): ScoreAction {
  const blocked = scoreBlockedReason(execution);
  if (blocked !== null) return { kind: 'blocked', reasonKey: blocked };
  return mayCompute ? { kind: 'compute' } : { kind: 'none' };
}

/** Why a violation has no example rows, in words. Three absences, three sentences.
 *
 * The whole reason `SampleState` is an enum rather than `sample_ref: string | null` is that a UI which
 * renders the three identically misleads an analyst — and this function is where that either holds or
 * quietly stops holding. `withheld` is the customer's own decision, `expired` is retention doing its
 * job, `none_captured` is our configuration or the rule's shape. Those three send a person to three
 * different places.
 *
 * Returns an i18n key rather than prose, so the wording lives in the catalogue and this stays testable
 * without formatting anything.
 */
export function sampleAbsenceKey(state: SampleState): string {
  switch (state) {
    case 'withheld':
      return 'violations.sample.withheld';
    case 'expired':
      return 'violations.sample.expired';
    case 'none_captured':
      return 'violations.sample.noneCaptured';
    default:
      return 'violations.sample.available';
  }
}

/** The tenant's sample mode, in words, and whether they chose it.
 *
 * `declared` is carried separately by the contract precisely because it is not derivable: an
 * undeclared tenant reports the *default* mode and so does a tenant who chose it deliberately. A
 * screen that dropped the distinction would tell an operator their customer had agreed to something
 * nobody had asked them.
 */
export function samplePolicyKey(mode: SampleMode, declared: boolean): string {
  if (!declared) return 'violations.policy.undeclared';
  switch (mode) {
    case 'zero_row':
      return 'violations.policy.zeroRow';
    case 'customer_held':
      return 'violations.policy.customerHeld';
    default:
      return 'violations.policy.maskedSample';
  }
}

/** The masked columns present in a sample, in a stable order, paired with their source column.
 *
 * Derived from the rows rather than from `columns`, because the rows are what will be rendered and a
 * header built from a different source could name a column no cell holds. The suffixes are the
 * contract's: `_digest` is an `md5()` computed at the source and `_preview` is first-two/last-two.
 *
 * **There is no branch here that could produce an unmasked header**, which is the same structural
 * argument the artifact makes one layer down: every key in the data carries a suffix, so a key without
 * one cannot appear in the output of this function either.
 */
export function sampleColumns(
  rows: readonly Readonly<Record<string, string | null>>[],
): readonly {
  readonly source: string;
  readonly digest: string;
  readonly preview: string;
}[] {
  const sources: string[] = [];
  for (const row of rows) {
    for (const alias of Object.keys(row)) {
      const source = alias.endsWith('_digest')
        ? alias.slice(0, -'_digest'.length)
        : alias.endsWith('_preview')
          ? alias.slice(0, -'_preview'.length)
          : undefined;
      if (source !== undefined && !sources.includes(source)) sources.push(source);
    }
  }
  return sources.map((source) => ({
    source,
    digest: `${source}_digest`,
    preview: `${source}_preview`,
  }));
}

/* The dot's hue per state, derived from the same decision `ruleTone` makes rather than restated.
 *
 * A second mapping here would be a second answer to "what colour is a draft", and the two would
 * drift the first time somebody adjusted one - which is the two-vocabularies failure this codebase
 * records at several sites. `ruleTone` returns a `BadgeTone`, and every tone but `neutral` names a
 * status token directly; `neutral` has no `--status-neutral`, so it maps to the unknown hue, which
 * is the one already reserved for "nothing to report". */
export function stateHue(state: RuleState): string {
  const tone = ruleTone(state);
  return tone === 'neutral' ? 'var(--status-unknown)' : `var(--status-${tone})`;
}

/* What a rule reaches, and when it last measured anything.
 *
 * Both columns replace `owner` in the rules table, and both are *derived* from reads the screen
 * already makes rather than from new endpoints. That is what makes them honest: the numbers come from
 * the platform's own resolution, not from a client re-implementing selector matching.
 *
 * **`Execution.targets` is the source, and it is the resolved set.** Its own docstring says why the
 * server stores it: "a selector is a question whose answer changes as tables are created, tagged and
 * renamed; storing the question would leave 'why did this run cover 41 tables when the last covered
 * 43' unanswerable". So a target names a `rule_version_id` and a `dataset_id`, and joining it through
 * the bindings' version-to-rule mapping gives an exact per-rule dataset count for a run that actually
 * happened. No globbing, no approximation.
 *
 * **The bound is the executions page.** `useExecutions` reads the recent ones, so a rule whose last
 * run predates that window reads as never-run. `RuleReach.beyondWindow` is what stops that being a
 * silent lie: the cell says "not in recent runs" rather than "never".
 */

/** What a rule is bound to, as one of four distinguishable answers. */
export type RuleReach =
  /** No enabled binding names this rule. Not the same as bound-to-nothing: there is no binding. */
  | { readonly kind: 'unbound' }
  /** Bound, and a run has resolved it to this many datasets. Exact, from the stored target set. */
  | { readonly kind: 'resolved'; readonly datasets: number; readonly bindings: number }
  /** Bound, and no run in the window resolved it — so the dataset count is unknown, not zero. */
  | { readonly kind: 'unresolved'; readonly bindings: number }
  /** The bindings read failed. About us, not about the rule. */
  | { readonly kind: 'unread' };

/** When a rule last measured anything, or why it never has. */
export type RuleLastRun =
  | { readonly kind: 'unread' }
  /** Ran, and this is when. `datasets` is what that run covered *for this rule*. */
  | { readonly kind: 'ran'; readonly at: string; readonly datasets: number }
  /** Never ran, and the rule's own state is the reason. Each state has its own sentence. */
  | { readonly kind: 'never'; readonly because: RuleState }
  /** Never ran within the executions the screen read. Not "never". */
  | { readonly kind: 'beyondWindow' };

/* Rule versions to rule ids, from the enabled bindings.
 *
 * A `Map` rather than a lookup per row: the rules table renders every rule and the bindings list is
 * read once, so building the index once is the difference between one pass and one pass per row.
 */
export function versionsByRule(
  bindings: readonly RuleBinding[],
): ReadonlyMap<string, string> {
  return new Map(bindings.map((binding) => [binding.rule_version_id, binding.rule_id]));
}

export function reachOf(
  rule: Rule,
  bindings: readonly RuleBinding[] | undefined,
  executions: readonly Execution[] | undefined,
): RuleReach {
  if (bindings === undefined) return { kind: 'unread' };

  const own = bindings.filter((binding) => binding.rule_id === rule.id);
  if (own.length === 0) return { kind: 'unbound' };

  /* Datasets from the most recent run that covered this rule, not the union across runs. A union
   * would answer "every table this rule has ever touched", which is a different question and grows
   * monotonically — a rule narrowed last week would still report the wider set. */
  const versions = new Set(own.map((binding) => binding.rule_version_id));
  const latest = latestRunFor(versions, executions);
  if (latest === null) return { kind: 'unresolved', bindings: own.length };
  return { kind: 'resolved', datasets: latest.datasets, bindings: own.length };
}

export function lastRunOf(
  rule: Rule,
  bindings: readonly RuleBinding[] | undefined,
  executions: readonly Execution[] | undefined,
): RuleLastRun {
  if (executions === undefined || bindings === undefined) return { kind: 'unread' };

  const versions = new Set(
    bindings
      .filter((binding) => binding.rule_id === rule.id)
      .map((binding) => binding.rule_version_id),
  );
  /* **No enabled binding does not mean never ran**, and the short-circuit that used to be here
   * implied otherwise. `GET /v1/rules/bindings` lists *enabled* bindings, so a rule whose binding
   * was disabled yesterday has none today and may still have run last week — "never" would be a
   * claim about the past drawn from a present-tense read. The window is the honest bound either way.
   *
   * `reachOf` answers `unbound` for the same input, and that is not a contradiction: "bound to" *is*
   * a present-tense question. The two columns read one payload and answer different tenses, which is
   * the distinction the tests beside this file pin. */
  const latest = latestRunFor(versions, executions);
  if (latest !== null) {
    return { kind: 'ran', at: latest.at, datasets: latest.datasets };
  }

  /* **Never-run is explained by the rule's state, which is the whole value of this column.**
   * "Never" alone sends a reader to look for an execution that could not have existed. Only an
   * *active* rule that has not run is a scheduling question, and only then is the executions window
   * the thing that might be hiding the answer.
   *
   * **The draft sentence states a fact rather than a next step, and that is a correction.** It read
   * "publish it, then two people sign it" — written when authoring left a rule with no version. It
   * does not any more: `CreateRuleForm` publishes 1.0.0 in the same submission, so a draft normally
   * *is* published and the instruction pointed at a control that needs no pressing. A draft with no
   * version does still exist — it is what the create-then-publish failure path leaves — and this
   * column cannot tell the two apart, because it is given `bindings` and never versions and asking
   * for them would be a read per rule. So it says the thing true of both: a draft has not been
   * signed. */
  if (rule.state !== 'active') return { kind: 'never', because: rule.state };
  return { kind: 'beyondWindow' };
}

/* The most recent finished execution covering any of these rule versions, and what it covered.
 *
 * Ordered by `finished_at` rather than by the array's order: `useExecutions` returns newest first
 * today, and relying on that would make this wrong the day the route grows a sort parameter.
 * Unfinished runs are skipped — an execution still in flight has not measured anything yet, and
 * reporting its start as a last run would show a time before any result existed.
 */
function latestRunFor(
  versions: ReadonlySet<string>,
  executions: readonly Execution[] | undefined,
): { readonly at: string; readonly datasets: number } | null {
  if (executions === undefined) return null;
  let best: { readonly at: string; readonly datasets: number } | null = null;

  for (const execution of executions) {
    if (execution.finished_at === null) continue;
    const covered = new Set(
      execution.targets
        .filter((target) => versions.has(target.rule_version_id))
        .map((target) => target.dataset_id),
    );
    if (covered.size === 0) continue;
    if (best === null || execution.finished_at > best.at) {
      best = { at: execution.finished_at, datasets: covered.size };
    }
  }
  return best;
}

/* The rule lifecycle as a reader travels it: five stages, three of which are `RuleState` values.
 *
 * **Two of these are stages rather than states, and the type says which.** `RuleState` has `draft`,
 * `active` and `deprecated`. Publishing produces a `RuleVersion` and requesting activation opens an
 * approval request in `kernel.approval` — both are real, observable steps a rule passes through, and
 * neither is a value `rule.state` can hold.
 *
 * An earlier strip drew three dots with the other two as gate *labels* between them. That was honest
 * about the state machine and wrong about the journey: it made four steps read as two. Drawing five
 * and marking the two that are not states is honest about both, and `isState` is what a caller uses
 * to say so on hover rather than leaving a reader to look for a `Published` badge.
 *
 * `hue` comes from `stateHue` for the three real states so the strip and the badges cannot disagree
 * about what colour a draft is. The two stages take the accent, which is this design's "in progress"
 * colour and is used for no state.
 */
export interface LifecycleStage {
  /** The copy key for the label. */
  readonly labelKey: string;
  /** The copy key for the hover text. */
  readonly meaningKey: string;
  readonly hue: string;
  /** `true` when this is a value `rule.state` can hold, `false` for a stage between two states. */
  readonly isState: boolean;
  /** The `rule.state` value, for the three stages that are one — what the strip counts and filters
   * by. Absent for the two gates, which no rule row can hold. */
  readonly state?: RuleState;
}

export const LIFECYCLE_STAGES: readonly LifecycleStage[] = [
  {
    labelKey: 'rules.state.draft.label',
    meaningKey: 'rules.state.draft.meaning',
    hue: stateHue('draft'),
    isState: true,
    state: 'draft',
  },
  {
    labelKey: 'rules.stage.published.label',
    meaningKey: 'rules.stage.published.meaning',
    hue: 'var(--accent)',
    isState: false,
  },
  {
    labelKey: 'rules.stage.awaitingApproval.label',
    meaningKey: 'rules.stage.awaitingApproval.meaning',
    hue: 'var(--status-warn)',
    isState: false,
  },
  {
    labelKey: 'rules.state.active.label',
    meaningKey: 'rules.state.active.meaning',
    hue: stateHue('active'),
    isState: true,
    state: 'active',
  },
  {
    labelKey: 'rules.state.deprecated.label',
    meaningKey: 'rules.state.deprecated.meaning',
    hue: stateHue('deprecated'),
    isState: true,
    state: 'deprecated',
  },
];

/* A binding's standing: is it running, and when did it last measure anything.
 *
 * The reference this follows shows `Active · 0 violations today`, `Waiting on approval — has not run`
 * and `Published — approval not requested` in one column. Three of those four readings are real here;
 * the violation count is not, because there is no per-binding violation figure without a request per
 * row. So the cell reports the standing and the recency, which is what makes the row actionable.
 *
 * **A disabled binding is its own reading and not a rule state.** `enabled` is the binding's own
 * switch: a disabled binding on an active rule runs nothing, and rendering the rule's `active` there
 * would say the opposite of what is happening.
 */
export type BindingStanding =
  /** The binding is switched off. Nothing runs, whatever the rule's state. */
  | { readonly kind: 'disabled' }
  /** The rule is not active, so the binding cannot run yet. The rule's state is the reason. */
  | { readonly kind: 'ruleNotActive'; readonly state: RuleState }
  /** Enabled, on an active rule, and a run has covered it. */
  | { readonly kind: 'running'; readonly at: string }
  /** Enabled, on an active rule, and no run in the window covered it. */
  | { readonly kind: 'notRunYet' }
  /** The rules or executions read failed, so the standing is unknown. */
  | { readonly kind: 'unread' };

export function standingOf(
  binding: RuleBinding,
  rule: Rule | undefined,
  executions: readonly Execution[] | undefined,
): BindingStanding {
  if (!binding.enabled) return { kind: 'disabled' };
  /* An unresolved rule is `unread` rather than assumed active: `useRules` is a bounded search read,
   * so a binding whose rule is past the page is a rule we did not fetch, not a rule that is missing.
   * Claiming a standing from that absence would be a statement about a rule nobody looked at. */
  if (rule === undefined || executions === undefined) return { kind: 'unread' };
  if (rule.state !== 'active') return { kind: 'ruleNotActive', state: rule.state };

  const covered = executions
    .filter(
      (execution) =>
        execution.finished_at !== null &&
        execution.targets.some(
          (target) => target.rule_version_id === binding.rule_version_id,
        ),
    )
    .map((execution) => execution.finished_at)
    .filter((at): at is string => at !== null);

  if (covered.length === 0) return { kind: 'notRunYet' };
  /* The latest by value rather than by array order, for `latestRunFor`'s reason: the route's ordering
   * is not part of its contract. */
  return { kind: 'running', at: covered.reduce((a, b) => (a > b ? a : b)) };
}

/* ---------------------------------------------------------------------------------------------
 * The executions listing, in words a reader who does not know the platform can act on.
 * ------------------------------------------------------------------------------------------- */

/** What one run found, as the Result column states it.
 *
 * **Five outcomes, and the two that look alike are the reason this is a union.** `clean` is "we
 * checked and found nothing"; `notMeasured` is "nothing has been checked". The reference design puts
 * the distinction in its own footnote — *a queued run never reads as a clean one* — and a single
 * nullable number cannot hold it: `0` would serve both, and the safe-looking one is the wrong one.
 *
 * `running` outranks a total that already exists, deliberately. A run halfway through has genuinely
 * found what it has found, and `partial` carries that — but the headline while tasks are still
 * arriving is that the answer is not final, because a reader who sees "0 violations" on a moving run
 * will remember the zero and not the movement.
 */
export type RunOutcome =
  | { readonly kind: 'running' }
  | { readonly kind: 'notMeasured' }
  | { readonly kind: 'clean'; readonly partial: boolean }
  | { readonly kind: 'violations'; readonly count: number; readonly partial: boolean }
  /* `found` is what the run measured before it stopped. **Not dropped, and that is a correction
     found by running this against real data.** An execution can be marked failed for a reason its
     tasks are not — the six-hour adoption clears a stalled run — so a run whose one task succeeded
     and found 4 violations arrives here as `failed` with a real count. Leading with the failure is
     right; *hiding* the four is not, and the first version's own argument was about the reassuring
     direction only: "0 violations on a run that broke" would be too comforting, and "we found
     nothing to tell you" about four violations is worse. */
  | { readonly kind: 'failed'; readonly found: number | null };

/** The Result cell, from the run and its findings.
 *
 * `findings` is optional because the two reads land independently: until the totals arrive there is
 * no number, and the honest answer is the run's own state rather than a zero.
 */
export function runOutcome(
  execution: { readonly state: string },
  findings?: { readonly violations: number | null; readonly partial: boolean },
): RunOutcome {
  if (execution.state === 'running' || execution.state === 'pending') {
    /* Pending *is* running for this cell's purpose — both mean "not final" — while the Started
       column keeps them apart, which is where the difference belongs: one has begun and one has
       not. */
    return { kind: 'running' };
  }
  if (execution.state === 'failed' || execution.state === 'cancelled') {
    /* **Failure leads, and a real finding is not thrown away with it.** Leading with "0 violations"
       on a run that broke would be the most reassuring available rendering of the least reassuring
       fact — but a run marked failed by the stalled-run adoption may have tasks that genuinely
       succeeded, and reporting only "did not finish" about four measured violations is the same
       mistake pointing the other way. `null` when nothing was measured, and only then. */
    const found = findings?.violations ?? null;
    return { kind: 'failed', found: found === 0 ? null : found };
  }
  /* `== null` catches both the absent read and a null total, which are the same answer here: no
     number to report. eslint refuses the spelled-out pair as a less concise optional chain. */
  if (findings?.violations == null) return { kind: 'notMeasured' };
  return findings.violations === 0
    ? { kind: 'clean', partial: findings.partial }
    : { kind: 'violations', count: findings.violations, partial: findings.partial };
}

/** Tone for the Result pill. Total over the union, so a sixth outcome is a compile error. */
export function outcomeTone(outcome: RunOutcome): BadgeTone {
  const tones = {
    running: 'info',
    /* `unknown`, the hue reserved for "we could not tell" — never the green that means checked and
       clean, and never the amber that means something needs looking at. Nothing is wrong with a run
       that has not started. */
    notMeasured: 'unknown',
    clean: 'ok',
    violations: 'danger',
    failed: 'danger',
  } as const satisfies Record<RunOutcome['kind'], BadgeTone>;
  return tones[outcome.kind];
}

/** The copy key for the Result pill. */
export function outcomeKey(outcome: RunOutcome): string {
  return `executions.outcome.${outcome.kind}`;
}

/** When a run started, in the words the reference uses.
 *
 * **Relative, because "did the nightly sweep run" is the question this column exists for** and
 * `31/08/2026, 06:00:12` makes a reader do date arithmetic to answer it. Today and yesterday are
 * named; anything older gets a day and month, which is enough to place it without a year most
 * readers do not need.
 *
 * `null` in, `null` out — a run that has been accepted and not started. That is a real state on a
 * 202 route and the caller renders "Queued" for it, never a dash, which would read as missing data
 * about a run that had started.
 */
export function startedLabel(startedAt: string | null, now: Date): string | null {
  if (startedAt === null) return null;
  const at = new Date(startedAt);
  if (Number.isNaN(at.getTime())) return startedAt;

  const time = at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  /* Compared as calendar days rather than as elapsed hours: 23:50 yesterday is "yesterday" even
     though it is forty minutes ago, and 00:10 today is "today" for the same reason. Hours-since
     would call both "an hour ago", which is true and answers a different question. */
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.floor((midnight.getTime() - startOfDay(at).getTime()) / DAY_MS);

  if (days === 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  return `${at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(at: Date): Date {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate());
}

/** What a run covered, in one phrase.
 *
 * **A table's name where there is one, a count where there are many** — which is what a reader
 * scanning the list actually needs, and what the previous first column did not give: it showed eight
 * hex characters of a UUID, so the answer to "which run touched sales.orders" was not on the page.
 * The id stays as the secondary line, because it is what a run is quoted by in an incident review.
 */
export type RunScope =
  | { readonly kind: 'one'; readonly name: string }
  | { readonly kind: 'many'; readonly count: number }
  | { readonly kind: 'unresolved' };

export function runScope(execution: {
  readonly targets: readonly { readonly qualified_name: string }[];
}): RunScope {
  const names = new Set(execution.targets.map((target) => target.qualified_name));
  /* `unresolved` rather than "0 tables": a run's targets are stored when the plan is made, so an
     empty set means the plan is not written yet — not that the run covers nothing. The route refuses
     a plan covering nothing, so "nothing" is not a state a stored run can be in. */
  if (names.size === 0) return { kind: 'unresolved' };
  const [only] = [...names];
  return names.size === 1 && only !== undefined
    ? { kind: 'one', name: only }
    : { kind: 'many', count: names.size };
}

/* ---------------------------------------------------------------------------------------------
 * One run's own page: the four figures, and the ledger of what each task did.
 * ------------------------------------------------------------------------------------------- */

/** How long a task took, or `null` when that is not yet a fact.
 *
 * **`null` for a task that has not finished, and that is not the same as a duration of zero.** The
 * design renders an em dash for it. A running task has been going for some time and reporting that
 * as its *duration* would mean the column changed meaning per row — one cell answering "how long did
 * this take" and its neighbour "how long has this been going".
 */
export function taskDuration(task: {
  readonly started_at: string | null;
  readonly finished_at: string | null;
}): string | null {
  if (task.started_at === null || task.finished_at === null) return null;
  const ms = new Date(task.finished_at).getTime() - new Date(task.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${String(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  /* `4 m 12 s`, padded, as the reference sets it. Padded so a column of them aligns on the seconds
     without needing tabular figures for the unit letters. */
  return `${String(minutes)} m ${String(seconds % 60).padStart(2, '0')} s`;
}

/** What a task measured, as one identifier.
 *
 * `schema.table.column` where the rule names a column, `schema.table` where it does not — which is
 * the real answer for a `not_null` bound with no target column, since planning then checks every
 * column the table has. Several columns are joined, because a multi-column rule genuinely measured
 * all of them and picking the first would name a target that is not the whole subject.
 */
export function taskTarget(task: {
  readonly qualified_name: string;
  readonly columns: readonly string[];
}): string {
  return task.columns.length === 0
    ? task.qualified_name
    : task.columns.map((column) => `${task.qualified_name}.${column}`).join(', ');
}

/** One of the four figures above a run's task ledger. */
export interface RunStat {
  readonly key: string;
  /** `null` renders as an em dash at the figure's own size — see `.lq-runstat__value--absent`. */
  readonly value: string | null;
  readonly note: string;
  readonly tone?: 'danger' | 'ok';
}

/** Tasks, rows read, violations, duration — with a sentence under each saying what it covers.
 *
 * **The note is the honest half and the reason this is a model function rather than four literals.**
 * A violations figure of `—` is meaningless without "unknown for the five failed tasks" beside it,
 * and a rows figure of 412 K is misleading without "only the first task finished". The design writes
 * a different sentence under each figure for each state of the run, and every one of them is a
 * statement about coverage rather than a caption.
 */
export function runStats(
  progress: {
    readonly total: number;
    readonly succeeded: number;
    readonly failed: number;
    readonly cancelled: number;
  },
  findings:
    | {
        readonly violations: number | null;
        readonly rows_evaluated: number | null;
        readonly measured_tasks: number;
      }
    | undefined,
  elapsed: string | null,
): readonly RunStat[] {
  const running = Math.max(
    0,
    progress.total - progress.succeeded - progress.failed - progress.cancelled,
  );
  const measured = findings?.measured_tasks ?? 0;
  const unmeasured = Math.max(0, progress.total - measured);

  const taskNote = [
    progress.succeeded > 0 ? `${String(progress.succeeded)} succeeded` : null,
    progress.failed > 0 ? `${String(progress.failed)} failed` : null,
    progress.cancelled > 0 ? `${String(progress.cancelled)} cancelled` : null,
    running > 0 ? `${String(running)} still to run` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(', ');

  return [
    {
      key: 'tasks',
      value: String(progress.total),
      note: taskNote === '' ? 'None planned yet' : taskNote,
    },
    {
      key: 'rows',
      value: findings?.rows_evaluated == null ? null : compact(findings.rows_evaluated),
      /* Says which tasks the figure covers, never just "rows". A total across two of six tables and
         one across all six are different measurements wearing the same number. */
      note:
        findings?.rows_evaluated == null
          ? 'Nothing has been read'
          : unmeasured === 0
            ? 'Across every task'
            : `Across the ${String(measured)} that finished`,
    },
    {
      key: 'violations',
      value: findings?.violations == null ? null : findings.violations.toLocaleString(),
      note:
        findings?.violations == null
          ? unmeasured > 0 && progress.total > 0
            ? `Unknown — no task has finished`
            : 'Unknown until tasks finish'
          : unmeasured === 0
            ? findings.violations === 0
              ? 'Finished with nothing found'
              : 'Across every task'
            : `Known for the ${String(measured)} that finished only`,
      /* Red only when there is something to be red about. A zero in the error colour would make a
         clean run look like a finding at a glance, which is the whole point of the figure. */
      ...(findings?.violations != null && findings.violations > 0
        ? { tone: 'danger' as const }
        : {}),
    },
    {
      key: 'elapsed',
      value: elapsed,
      note: elapsed === null ? 'Not started' : running > 0 ? 'So far' : 'Start to finish',
    },
  ];
}

/** 1.7 M, 412 K, 640 — the reference's own scale.
 *
 * Abbreviated because these four figures sit in one row at 30px and a full `1,283,904` would set the
 * column width for the other three. One decimal below ten, none above, so `1.7 M` and `412 K` both
 * carry the two or three significant figures a reader compares runs by.
 */
function compact(value: number): string {
  if (value < 1000) return value.toLocaleString();
  if (value < 1_000_000) return `${trim(value / 1000)} K`;
  return `${trim(value / 1_000_000)} M`;
}

function trim(value: number): string {
  return value < 10 ? value.toFixed(1) : String(Math.round(value));
}

/** A run's name, as a reader quotes it.
 *
 * **`run-3cabfcd8`, not `run-8841`.** The reference names runs from a sequence this platform does not
 * have and could not add without a counter somebody has to keep — an execution's identity is its
 * UUID, deliberately, because two replicas accepting runs at the same instant must not race for a
 * number. What the reference is really doing is making a run *quotable*: "what did run 8841 find" is
 * a question somebody asks aloud. Eight hex characters of the id, with the same `run-` prefix, is
 * quotable and is the run's real name.
 *
 * Truncated for display only. Nothing derives meaning from the prefix — the link carries the whole
 * id — and a screen that matched on eight characters would be claiming a collision cannot happen.
 */
export function shortRunId(executionId: string): string {
  return `run-${executionId.slice(0, 8)}`;
}

/** The copy key naming a task's state in words.
 *
 * **A dynamic key is invisible to the copy gate, and this whole family was missing.** The state cell
 * on a run's page called this and there was no such message for any of the five states, so every
 * cell rendered the literal string `executions.task.failed`. `copy.test.ts` sweeps *static*
 * `t('…')` calls and cannot see a template, so the mechanism that catches it has to enumerate the
 * states — which `TASK_STATES` in the generated client already does. Tested against that rather
 * than against a second list here: two lists of the same enum is how one of them comes to be a
 * state short.
 */
export function taskStateKey(state: string): string {
  return `executions.task.${state}`;
}
